import { styled } from 'styled-components';
import * as React from 'react';
import Icon from '@/core/components/Icon';

export type MessageType = 'success' | 'error' | 'warning' | 'info' | 'form-error';

export interface MessagesBlockProps {
	type?: MessageType;
	standalone?: boolean;
	hidden?: boolean;
	dataQaId?: string;
	noIcon?: boolean;
	className?: string;
	children?: React.ReactNode;
	tabIndex?: number;
	role?: string;
	ariaLabel?: string; 
}

const Wrapper = styled.div<{
	$type: MessageType;
	$standalone: boolean;
	$hidden: boolean;
}>`
	display: flex;
	gap: 8px;
	padding: 16px;
	border-radius: 8px;
	align-items: center;

	${(p) => (p.$standalone ? 'padding: 24px 16px; border-radius: 16px;' : '')};

	${(p) =>
		p.$type === 'success'
			? 'background-color: #DFFF9D; color: #27430A;'
			: p.$type === 'error' || p.$type === 'form-error'
				? 'background-color: #FFB4B5; color: #3B1D19;'
				: p.$type === 'warning'
					? 'background-color: #FFFCE1; color: #362F08;'
					: 'background-color: #BBF9FF; color: #0F0F41;'};

	${(p) => (p.$type === 'form-error' ? 'width: 100%;' : '')};

	${(p) => (p.$hidden ? 'display: none;' : '')};

	${(p) => (p.$type === 'form-error' ? '&:focus { outline: none; box-shadow: none; }' : '')};

	@media (min-width: 768px) {
		gap: 16px;
		border-radius: 12px;

		${(p) => (p.$standalone ? 'padding: 32px; border-radius: 24px;' : '')}
	}
`;

const LeadingIcon = styled(Icon)<{ $type?: MessageType }>`
	flex: 0 0 24px;
	display: inline-flex;
	${(p) =>
		p.$type === 'form-error'
			? `color: #DF3522 !important; align-self: flex-start; & > svg { width: 24px; height: 24px; }`
			: ''}
`;

const Content = styled.div`
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;

	@media (min-width: 768px) {
		font-size: 16px;
		line-height: 24px;
	}
`;

function getRole(type: MessageType): 'alert' | 'status' | undefined {
	switch (type) {
		case 'error':
		case 'warning':
		case 'form-error':
			return 'alert';
		case 'success':
		case 'info':
		default:
			return 'status';
	}
}

// Best-effort icon mapping. If an icon doesn't exist in the sprite, Icon will render null.
function getIconName(type: MessageType): string {
	// Prefer conventional names; fall back to the type itself.
	switch (type) {
		case 'success':
			return 'check-circle';
		case 'error':
			return 'x-circle';
		case 'form-error':
		case 'warning':
			return 'alert-triangle';
		case 'info':
		default:
			return 'info';
	}
}

const MessagesBlock = ({
	type = 'info',
	standalone = false,
	hidden = false,
	dataQaId,
	noIcon = false,
	className,
	children,
	tabIndex,
			role: roleOverride,
			innerRef,
			ariaLabel,
		}: MessagesBlockProps & { innerRef?: React.RefObject<HTMLDivElement | null> }) => {
	const computedRole = getRole(type);
	const roleToApply = roleOverride ?? computedRole;
	const iconName = getIconName(type);

	return (
		<Wrapper
			className={className}
			data-qa-id={dataQaId}
			$type={type}
			$standalone={standalone}
			$hidden={hidden}
			role={roleToApply}
			ref={innerRef}
			tabIndex={tabIndex}
			aria-label={ariaLabel}
		>
			{!noIcon ? <LeadingIcon name={iconName} aria-hidden="true" $type={type} /> : null}
			<Content aria-hidden={ariaLabel ? true : undefined}>{children}</Content>
		</Wrapper>
	);
};

export default MessagesBlock;
